/**
 * aethereum claim — attach this project's accountless (anon) room to your account.
 *
 * Claiming needs a real browser session (sign-in), so the CLI cannot do it
 * headlessly: it prints the claim URL for the active room and a one-line
 * explanation. After signing in + claiming, the user runs 'aethereum login' so
 * the CLI can invite + manage on the now-owned room.
 *
 * Read-only + offline-safe: it only reads local config and prints a URL.
 */

import { homedir } from "node:os";
import { resolveProject } from "../hive/config-store.js";

export interface ClaimOptions {
  url?: string;
  home?: string;
  cwd?: string;
  env?: Record<string, string | undefined>;
}

const DEFAULT_BASE = "https://www.aethereum.dev";

export async function runClaim(options: ClaimOptions = {}): Promise<void> {
  const home = options.home ?? homedir();
  const cwd = options.cwd ?? process.cwd();
  const env = options.env ?? (process.env as Record<string, string | undefined>);
  const project = resolveProject(cwd, home);

  const base = (options.url ?? env["AETHEREUM_SYNC_API_URL"] ?? project?.url ?? DEFAULT_BASE)
    .replace(/\/$/, "")
    .replace(/^https:\/\/aethereum\.dev(?=$|\/)/, "https://www.aethereum.dev");

  if (!project) {
    console.error("No room wired here. Run 'aethereum init' first.");
    process.exit(1);
    return;
  }
  if (!project.roomId) {
    console.error(
      "This room's id is unknown locally (older config). Run 'aethereum reconnect' to recover it (keeps your existing room), then 'aethereum claim'.",
    );
    process.exit(1);
    return;
  }

  if (!project.anon) {
    console.log("\n  [--] this room is already attached to an account; nothing to claim.\n");
    return;
  }

  const next = encodeURIComponent(`/claim?room=${project.roomId}`);
  const claimUrl = `${base}/signin?next=${next}`;
  console.log(`
Claim this room to keep it forever + invite your team.

  Claiming needs a browser sign-in, so open this URL:
    ${claimUrl}

  After you sign in and claim, mint a CLI token at ${base}/account and run:
    aethereum login

  Then 'aethereum invite <email>' and 'aethereum room list' will work.
`);
}
